import { useGetGlobalFiltersLimit } from '@cs-cms/state-management';
import { CpsGridView, EditingViewState } from '@cs-cms/types';

interface ViewData {
  views: CpsGridView[];
  selectedViewId?: number;
}

interface PermissionState {
  isCreateLoading: boolean;
  isWriteLoading: boolean;
  hasWrite: boolean;
  hasCreate: boolean;
}

interface EditingData {
  viewState: EditingViewState;
  hasUnsavedChanges: boolean;
}

interface ValidationResult {
  hasIncompleteFields: boolean;
  hasIncompleteGlobalFilters: boolean;
  hasNoData: boolean;
  hasIncompleteData: boolean;
}

const useViewMetadata = (viewData: ViewData) => {
  const currentView = viewData.views.find(view => view.id === viewData.selectedViewId);
  const isDefaultView = currentView?.isDefault ?? false;

  return { currentView, isDefaultView };
};

const usePermissionStates = (permissions: PermissionState) => {
  const canCreateView = !permissions.isCreateLoading && permissions.hasCreate;
  const canModifyView = !permissions.isWriteLoading && permissions.hasWrite;

  return { canCreateView, canModifyView };
};

const useDataValidation = (editingViewState: EditingViewState): ValidationResult => {
  const hasIncompleteFields = editingViewState.fields.some((field: any) => !field.id || !field.name);

  const hasIncompleteGlobalFilters = editingViewState.globalFilters.some(
    (field: any) => field.id < 0 || !field.name || field.name === ''
  );

  const hasIncompleteData = hasIncompleteFields || hasIncompleteGlobalFilters;
  const hasNoData = editingViewState.fields.length === 0;

  return { hasIncompleteFields, hasIncompleteGlobalFilters, hasNoData, hasIncompleteData };
};

export const useSystemViewManagementButtonStates = (
  viewData: ViewData,
  editingData: EditingData,
  permissions: PermissionState,
  indexId?: number
) => {
  const { data: globalFiltersLimit, isLoading: isGlobalFiltersLimitLoading } = useGetGlobalFiltersLimit(indexId);

  const { currentView, isDefaultView } = useViewMetadata(viewData);
  const { canCreateView, canModifyView } = usePermissionStates(permissions);
  const { hasIncompleteFields, hasIncompleteGlobalFilters, hasNoData, hasIncompleteData } = useDataValidation(
    editingData.viewState
  );

  const globalFiltersExceeded =
    !isGlobalFiltersLimitLoading &&
    globalFiltersLimit !== undefined &&
    editingData.viewState.globalFilters.length > globalFiltersLimit;

  return {
    currentView,
    isDefaultView,
    isCreateDisabled: permissions.isCreateLoading || !canCreateView,
    isRenameDisabled: !viewData.selectedViewId || permissions.isWriteLoading || !canModifyView,
    isDeleteDisabled: !viewData.selectedViewId || isDefaultView || permissions.isWriteLoading || !canModifyView,
    isSetDefaultDisabled: !viewData.selectedViewId || isDefaultView || permissions.isWriteLoading || !canModifyView,
    hasIncompleteData,
    isSaveDisabled:
      hasIncompleteFields ||
      hasIncompleteGlobalFilters ||
      hasNoData ||
      !editingData.hasUnsavedChanges ||
      !canModifyView ||
      globalFiltersExceeded,
    isGlobalFiltersLimitExceeded: globalFiltersExceeded
  };
};
